import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Paperclip, Loader2, Bot, Trash2, Lightbulb } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const getSession = () => {
  let s = localStorage.getItem("copilot_session");
  if (!s) { s = "cp-" + Math.random().toString(36).slice(2, 12); localStorage.setItem("copilot_session", s); }
  return s;
};

const fileToPayload = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const isImage = file.type.startsWith("image/");
      resolve({
        type: isImage ? "image" : "document",
        name: file.name,
        mime: file.type,
        data: isImage ? result : String(result).split(",", 2)[1] || "",
        encoding: "base64",
        _preview: isImage ? result : null,
      });
    };
    if (file.type.startsWith("image/")) reader.readAsDataURL(file);
    else reader.readAsDataURL(file);
  });

export const Copilot = ({ context = {}, onApplied }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(getSession);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSug, setLoadingSug] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      api.copilotHistory(session)
        .then((h) => setMessages(h.map((m) => ({ role: m.role, text: m.text }))))
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line

  const ctxKey = JSON.stringify(context);
  useEffect(() => {
    const hasEntity = context.workspace_id || context.proposal_id || context.business_id;
    if (!open || !hasEntity) { setSuggestions([]); return; }
    setLoadingSug(true);
    api.copilotSuggestions({ session_id: session, context })
      .then((r) => setSuggestions(r.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSug(false));
  }, [open, ctxKey]); // eslint-disable-line

  const refreshSuggestions = () => {
    const hasEntity = context.workspace_id || context.proposal_id || context.business_id;
    if (!hasEntity) return;
    api.copilotSuggestions({ session_id: session, context }).then((r) => setSuggestions(r.suggestions || [])).catch(() => {});
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const payloads = await Promise.all(files.map(fileToPayload));
    setAttachments((a) => [...a, ...payloads]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async (overrideText) => {
    if (busy) return;
    const text = (overrideText != null ? overrideText : input).trim();
    if (!text && attachments.length === 0) return;
    const userLabel = text || `📎 ${attachments.map((a) => a.name).join(", ")}`;
    setMessages((m) => [...m, { role: "user", text: userLabel }]);
    setInput("");
    const atts = attachments;
    setAttachments([]);
    setBusy(true);
    try {
      const res = await api.copilotChat({
        session_id: session,
        message: text,
        context,
        attachments: atts.map(({ _preview, ...rest }) => rest),
      });
      setMessages((m) => [...m, { role: "assistant", text: res.reply }]);
      if (res.changed) {
        toast.success("Copilot updated your workspace");
        onApplied?.(res);
        refreshSuggestions();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "The copilot hit a snag. Try again.";
      setMessages((m) => [...m, { role: "assistant", text: msg }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const clearChat = () => {
    const s = "cp-" + Math.random().toString(36).slice(2, 12);
    localStorage.setItem("copilot_session", s);
    setSession(s);
    setMessages([]); setAttachments([]); setInput("");
  };

  const where = context.page === "workroom" ? "this workroom"
    : context.page === "proposal-builder" ? "this proposal"
    : context.page === "business" ? "this client" : "your dashboard";

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="copilot-fab"
          className="fixed bottom-6 right-6 z-[300] w-14 h-14 rounded-full bg-primary-600 text-white shadow-2xl shadow-primary-900/40 flex items-center justify-center hover:bg-primary-500 hover:scale-105 transition-transform"
          title="DONDRÁE Core — your AI partner">
          <Sparkles size={24} />
        </button>
      )}

      {open && (
        <div className="fixed inset-y-0 right-0 z-[300] w-full sm:w-[420px] bg-[#0a0a0b]/95 backdrop-blur-xl border-l border-white/10 shadow-2xl flex flex-col fade-in" data-testid="copilot-panel">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-primary-600/20 to-transparent">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center"><Bot size={18} className="text-white" /></div>
              <div>
                <div className="text-sm font-bold text-white">DONDRÁE Core</div>
                <div className="text-[11px] text-zinc-400">Your AI business partner · {where}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearChat} className="p-2 text-zinc-500 hover:text-white transition" title="New chat" data-testid="copilot-clear"><Trash2 size={16} /></button>
              <button onClick={() => setOpen(false)} className="p-2 text-zinc-500 hover:text-white transition" data-testid="copilot-close"><X size={18} /></button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto hide-scrollbar p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-10 px-4">
                <Sparkles size={28} className="mx-auto text-primary-400 mb-3" />
                <p className="text-sm text-zinc-300 font-semibold mb-1">Hey Dondrae 👋</p>
                <p className="text-xs text-zinc-500 leading-relaxed">Paste your call/meeting notes, drop a whiteboard photo or a document, and I'll put it all in the right places — proposals, workrooms, and your CRM. I act automatically and tell you what I did.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`copilot-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-primary-600 text-white" : "bg-white/5 text-zinc-100 border border-white/10"}`}>{m.text}</div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-2 text-zinc-400 text-sm"><Loader2 size={15} className="animate-spin" /> Working on it…</div></div>
            )}
          </div>

          {attachments.length > 0 && (
            <div className="px-4 py-2 border-t border-white/10 flex flex-wrap gap-2">
              {attachments.map((a, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/5 rounded-lg pl-2 pr-1 py-1 text-xs text-zinc-300">
                  {a._preview ? <img src={a._preview} alt="" className="w-6 h-6 rounded object-cover" /> : <Paperclip size={12} />}
                  <span className="max-w-[120px] truncate">{a.name}</span>
                  <button onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400"><X size={13} /></button>
                </div>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-white/10">
            {(loadingSug || suggestions.length > 0) && (
              <div className="mb-2.5" data-testid="copilot-suggestions">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-primary-400 uppercase tracking-wider mb-1.5"><Lightbulb size={12} /> {loadingSug ? "Spotting gaps…" : "I can help with"}</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => send(s.prompt)} disabled={busy} className="px-2.5 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/25 text-primary-200 text-xs font-medium hover:bg-primary-500/20 transition disabled:opacity-50" title={s.prompt} data-testid="copilot-suggestion">{s.title}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-end gap-2 bg-white/5 rounded-2xl p-2 border border-white/10 focus-within:border-primary-500/50 transition">
              <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.docx,.txt,.md,.csv" onChange={pickFiles} className="hidden" data-testid="copilot-file-input" />
              <button onClick={() => fileRef.current?.click()} className="p-2 text-zinc-400 hover:text-white transition flex-shrink-0" title="Attach photo or document" data-testid="copilot-attach"><Paperclip size={18} /></button>
              <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1} placeholder="Paste notes or ask me to do something…" className="flex-1 bg-transparent text-sm text-white placeholder-zinc-500 resize-none outline-none py-2 max-h-32" data-testid="copilot-input" />
              <button onClick={send} disabled={busy || (!input.trim() && attachments.length === 0)} className="p-2.5 rounded-xl bg-primary-600 text-white hover:bg-primary-500 transition disabled:opacity-40 flex-shrink-0" data-testid="copilot-send">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
